import { diffLines, diffWordsWithSpace } from 'diff';
import { useDeferredValue, useMemo } from 'react';

import { isHtml, stripHtml } from '@/utils/emailContentDisplayUtils';
import { cn } from '@/utils/utils';

interface RedliningProps {
  className?: string;
  finalText?: string;
  initialText?: string;
  isGenerating?: boolean;
}

export const Redlining = ({ className, finalText, initialText, isGenerating }: RedliningProps) => {
  const initialIsHtml = useMemo(() => initialText && isHtml(initialText), [initialText]);
  const finalIsHtml = useMemo(() => finalText && isHtml(finalText), [finalText]);

  // Strip HTML for diffing to get clean text comparison
  const initialClean = useMemo(() => {
    if (!initialText) {
      return '';
    }
    return initialIsHtml ? stripHtml(initialText) : initialText;
  }, [initialText, initialIsHtml]);

  const finalClean = useMemo(() => {
    if (!finalText) {
      return '';
    }
    return finalIsHtml ? stripHtml(finalText) : finalText;
  }, [finalText, finalIsHtml]);

  // Defer streaming updates to reduce churn while keeping render logic pure.
  const deferredFinalClean = useDeferredValue(finalClean);
  const finalForDiff = isGenerating ? deferredFinalClean : finalClean;

  // Paragraph-level diff first; for paired add/remove paragraphs that are
  // substantially similar (>= ~50% shared words), do an inner word-level diff
  // so small edits stay readable. Whole-paragraph rewrites stay as one
  // strikethrough + one addition rather than a confetti of word-level diffs.
  const diffs = useMemo(() => {
    if (!initialClean || !finalForDiff) {
      return [];
    }

    const lineParts = diffLines(initialClean, finalForDiff, { newlineIsToken: true });
    const result: Array<{ added?: boolean; removed?: boolean; value: string }> = [];

    for (let i = 0; i < lineParts.length; i++) {
      const part = lineParts[i];
      if (!part) {
        continue;
      }
      const next = lineParts[i + 1];
      // Pair of removed-then-added: try inner word diff if lines look similar.
      if (part.removed && next?.added) {
        const aWords = new Set(part.value.toLowerCase().split(/\s+/).filter(Boolean));
        const bWords = next.value.toLowerCase().split(/\s+/).filter(Boolean);
        const overlap = bWords.filter((w) => aWords.has(w)).length;
        const similarity = bWords.length === 0 ? 0 : overlap / bWords.length;
        if (similarity >= 0.5) {
          result.push(...diffWordsWithSpace(part.value, next.value));
          i++;
          continue;
        }
      }
      result.push(part);
    }

    return result;
  }, [initialClean, finalForDiff]);

  return (
    <div className={cn('font-sans text-xs leading-relaxed whitespace-pre-wrap', className)}>
      {diffs?.length ? (
        diffs?.map((part, index) => {
          const content = part.value;

          if (part.added) {
            return (
              <span className="text-destructive decoration-destructive" key={index}>
                {content}
              </span>
            );
          } else if (part.removed) {
            return (
              <span className="text-primary decoration-primary line-through" key={index}>
                {content}
              </span>
            );
          } else {
            return <span key={index}>{content}</span>;
          }
        })
      ) : (
        <span className="text-secondary">{initialClean}</span>
      )}
    </div>
  );
};
